<template>
  <application-layout>
    <v-container>
      <v-layout
        v-if="viewLoading"
        column
        align-center
        class="py-5"
      >
        <v-layout column justify-center>
          <div class="text-h5 py-4">
            <v-progress-circular
              :size="70"
              :width="7"
              indeterminate
            />
          </div>
        </v-layout>
      </v-layout>
      <v-layout column v-else>
        <v-flex xs2 class="mb-2 mr-0" align-self-end>
          <v-btn
            data-testid="back-to-facility-btn"
            class="back-to-facility-btn mr-0"
            @click="handleBackToFacility"
            color="blue"
            outlined
            large
          >
            Back to Facility
          </v-btn>
        </v-flex>
        <tenant-details :facility="facility" @updateSuccess="getTenantInfo" />
        <v-tabs v-model="tab">
          <v-tab
            data-testid="active-rental-tab"
            class="active-rental-tab"
          >
            Active Rentals
          </v-tab>
          <v-tab
            data-testid="reservations-tab"
            class="reservations-tab"
          >
            Reservations
          </v-tab>
          <v-tab
            data-testid="facility-details-tab"
            class="facility-details-tab"
          >
            {{ facility.location_code }} Details
          </v-tab>
          <v-tab
            data-testid="facility-available-units-tab"
            class="facility-available-units-tab"
            @click="getFacilityInfo"
          >
            {{ facility.location_code }} Available Units
          </v-tab>
          </v-tabs>
          <v-tabs-items v-model="tab">
          <v-tab-item class="mt-4" eager>
            <tenant-rentals
              :convenienceFeeStatus="convenienceFeeStatus"
              @paymentSuccess="paymentSuccess"
              @billingUpdate="getLedgerInfo"
            />
          </v-tab-item>
          <v-tab-item class="mt-4" eager>
            <tenant-reservations
              :facility="facility"
              :reservations="reservations"
              :availableUnits="availableUnits"
              @tenantReservation="getTenantReservations"
              @reservationUpdated="getTenantReservations"
            />
          </v-tab-item>
          <v-tab-item class="mt-4" eager>
            <facility-details :facility="facility" />
          </v-tab-item>
          <v-tab-item class="mt-4" eager>
            <tenant-available-units
              :reservations="reservations"
              :availableUnits="availableUnits"
              @reservationCreated="getTenantReservations"
            />
          </v-tab-item>
        </v-tabs-items>
      </v-layout>
    </v-container>
  </application-layout>
</template>

<script>
import ApplicationLayout from "@/layouts/ApplicationLayout";
import FacilityDetails from "@/components/FacilityDetails";
import TenantAvailableUnits from "@/views/tenant/partials/TenantAvailableUnits";
import TenantDetails from "@/components/TenantDetails";
import TenantRentals from "@/components/tenant-rentals/TenantRentals";
import TenantReservations from "@/views/tenant/partials/TenantReservations";
import router from "@/router";
import { tenantIndexHeaders } from "@/helpers/tenant/tenant-index-data-table-headers";

export default {
  data() {
    return {
      loading: false,

      infoLoading: false,
      ledgersLoading: false,
      balanceDetailsLoading: false,
      convenienceFeeLoading: false,

      createReservationModal: false,
      paymentModal: false,

      facility: "",
      convenienceFeeStatus: "",
      balanceDetails: [],
      reservations: [],
      availableUnits: [],

      headers: tenantIndexHeaders,

      tab: 0
    };
  },

  methods: {
    getTenantInfo() {
      this.infoLoading = true;
      let payload = {
        facilityId: this.facilityId,
        tenantId: this.tenantId
      };

      this.$store.dispatch("getTenantInfo", payload)
        .then(() => {
          this.getTenantReservations();
          this.infoLoading = false;
          this.$store.commit("changingTenant", false);
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
          this.infoLoading = false;
          this.handleBackToFacility();
        });
    },

    async handleBackToFacility() {
      await this.$store.dispatch("handleResetTenant");
      router.push({ name: "facilityDetails" }).catch(() => {});
    },

    getTenantReservations() {
      this.loading = true;

      let payload = {
        facility_id: this.$route.params.facilityId,
        tenant_id: this.tenantId
      };

      this.$store.dispatch("getTenantReservations", payload)
        .then(resp => {
          if (resp.data && Array.isArray(resp.data)) {
            this.reservations = resp.data;
          } else if (resp.data && !Array.isArray(resp.data)) {
            this.reservations = [resp.data];
          }
          this.loading = false;
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
          this.loading = false;
        });
    },

    getFacilityInfo() {
      this.$store.dispatch("getFacility", this.facilityId)
        .then((resp) => {
          let facility = resp.data.facility;
          this.facility = facility;
          this.availableUnits = facility.units.parking ? facility.units.storage.concat(facility.units.parking) : facility.units.storage;
        });
    },

    getLedgerInfo() {
      this.ledgersLoading = true;

      let payload = {
        facilityId: this.facilityId,
        tenantId: this.$route.params.tenantId
      };

      this.$store.dispatch("getLedgerInfo", payload)
        .then(() => {
          this.ledgersLoading = false;
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
        });
    },

    getBalanceDetails() {
      this.balanceDetailsLoading = true;
      let payload = {
        facilityId: this.facilityId,
        tenantId: this.$route.params.tenantId
      };

      this.$store.dispatch("getBalanceDetails", payload)
        .then(() => {
          this.balanceDetailsLoading = false;
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
          this.balanceDetailsLoading = false;
        });
    },

    paymentSuccess() {
      this.getLedgerInfo();
      this.getBalanceDetails();
    },

    getConvenienceFeeLastWaived() {
      this.convenienceFeeLoading = true;
      let payload = {
        facilityId: this.facilityId,
        tenantId: this.$route.params.tenantId
      };

      this.$store.dispatch("convenienceFeeLastWaived", payload)
        .then(resp => {
          this.convenienceFeeStatus = resp.data.last_waived;
          this.convenienceFeeLoading = false;
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
          this.convenienceFeeLoading = false;
        });
    },

    getFacilityAlerts() {
      this.$store.dispatch("getFacilityAlerts", this.facilityId)
        .then(resp => {
          this.$store.commit("commitAlerts", resp.data.alerts);
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
        });
    },

    getMoveInCost() {
      if (this.$store.getters.unitId) {
        const payload = {
          facilityId: this.$route.params.facilityId
        };
        this.$store.dispatch("getMoveInCost", payload);
      }
    }
  },

  computed: {
    changingTenant() {
      return this.$store.state.tenant.changingTenant;
    },

    viewLoading() {
      return this.loading || this.ledgersLoading || this.infoLoading || this.balanceDetailsLoading;
    },

    tenantId() {
      return this.$route.params.tenantId
        ? +this.$route.params.tenantId
        : +this.$store.state.tenant.info.external_id;
    },
    facilityId() {
      return this.$route.params.facilityId;
    }
  },

  watch: {
    changingTenant() {
      if (this.$store.state.tenant.changingTenant) {
        this.getTenantInfo();
        this.getLedgerInfo();
        this.getBalanceDetails();
        this.getConvenienceFeeLastWaived();
      }
    },
    facilityId(val) {
      this.getFacilityInfo();
      if (val) {
        this.getFacilityAlerts();
      }
    },
    "$store.state.tenant.options": {
      handler() {
        this.getMoveInCost();
      },
      deep: true
    },
    "$store.state.reservations.invalidPromotion"(newState, oldState) {
      // if we detect an invalid promotion tied to a reservation,
      // we need to fetch the available promotions for the facility
      if (oldState !== false || newState !== true) return;
      if (!this.facility || !this.facility.id) return;
      this.$store.dispatch("getFacilityPromotions", { facilityId: this.facility.id });
    }
  },

  created() {
    this.getFacilityInfo();
    this.getFacilityAlerts();
    this.getTenantInfo();
    this.getLedgerInfo();
    this.getBalanceDetails();
    this.getConvenienceFeeLastWaived();
    this.$store.commit("clearCreditCard");
  },

  components: {
    FacilityDetails,
    ApplicationLayout,
    TenantAvailableUnits,
    TenantDetails,
    TenantReservations,
    TenantRentals
  }
};
</script>
